import { useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { PublicDisplay } from "../../domain/contracts/live-workspace";
import { LiveOverlay } from "../ui/LiveOverlay";

export function LiveOverlayPage() {
  const { id } = useParams({ from: "/overlay/$id" });
  const token = useRef(
    new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "",
  );
  const [display, setDisplay] = useState<PublicDisplay | null>(null);
  const [image, setImage] = useState<{ source: string; url: string } | null>(
    null,
  );
  const imageKey = display?.product?.imageUrl;
  const productId = display?.product?.id;
  const protectedImage = Boolean(imageKey && !/^https?:\/\//.test(imageKey));
  const imageSource = `${id}:${productId}:${imageKey}`;
  useEffect(() => {
    if (!protectedImage || !productId) return;
    const controller = new AbortController();
    let objectUrl: string | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryDelay = 500;
    const loadImage = () =>
      fetch(
        `/api/v1/live-displays/${encodeURIComponent(id)}/image?productId=${encodeURIComponent(productId)}`,
        {
          headers: { Authorization: `Bearer ${token.current}` },
          credentials: "omit",
          signal: controller.signal,
        },
      )
        .then(async (response) => {
          if (response.status >= 500) throw new Error("IMAGE_UNAVAILABLE");
          if (!response.ok) return;
          const blob = await response.blob();
          if (controller.signal.aborted) return;
          objectUrl = URL.createObjectURL(blob);
          setImage({ source: imageSource, url: objectUrl });
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            retryTimer = setTimeout(() => void loadImage(), retryDelay);
            retryDelay = Math.min(15_000, retryDelay * 2);
          }
        });
    void loadImage();
    return () => {
      controller.abort();
      clearTimeout(retryTimer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, productId, imageSource, protectedImage]);
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => {
      document.documentElement.style.background = "";
      document.body.style.background = "";
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    const headers = { Authorization: `Bearer ${token.current}` };
    let pending = false;
    let queued = false;
    let lastSuccess = performance.now();
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectDelay = 1000;
    let streamConnected = false;
    let lastHeartbeat = 0;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const refresh = async () => {
      if (pending) {
        queued = true;
        return;
      }
      pending = true;
      try {
        const response = await fetch(
          `/api/v1/live-displays/${encodeURIComponent(id)}/snapshot`,
          {
            headers,
            signal: controller.signal,
            cache: "no-store",
            credentials: "omit",
          },
        );
        if (!response.ok) {
          if (
            response.status === 404 ||
            response.status === 401 ||
            response.status === 403
          )
            setDisplay(null);
          return;
        }
        const value = (await response.json()) as { display: PublicDisplay };
        if (!controller.signal.aborted) {
          setDisplay(value.display);
          lastSuccess = performance.now();
        }
      } catch {
        /* polling จะลองใหม่; ไม่ขึ้น error บนภาพออกอากาศ */
      } finally {
        pending = false;
        if (queued && !controller.signal.aborted) {
          queued = false;
          void refresh();
        }
      }
    };
    const listen = async () => {
      try {
        const response = await fetch(
          `/api/v1/live-displays/${encodeURIComponent(id)}/events`,
          { headers, signal: controller.signal, credentials: "omit" },
        );
        if (!response.ok || !response.body) return;
        streamConnected = true;
        lastHeartbeat = performance.now();
        reconnectDelay = 1000;
        void refresh();
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (!controller.signal.aborted) {
          const chunk = await reader.read();
          if (chunk.done) break;
          lastHeartbeat = performance.now();
          buffer += decoder.decode(chunk.value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() ?? "";
          if (
            events.some((event) => /event: (refresh|resync)(\n|$)/.test(event))
          ) {
            // รวม burst ของ stock/order เป็น snapshot เดียว แทน refetch ต่อ event
            refreshTimer ??= setTimeout(() => {
              refreshTimer = undefined;
              void refresh();
            }, 150);
          }
        }
      } catch {
        /* snapshot polling สำรองเมื่อ Redis หรือ SSE ไม่พร้อม */
      } finally {
        streamConnected = false;
        if (!controller.signal.aborted) {
          reconnectTimer = setTimeout(() => void listen(), reconnectDelay);
          reconnectDelay = Math.min(15_000, reconnectDelay * 2);
        }
      }
    };
    void refresh();
    void listen();
    const timer = setInterval(() => {
      const now = performance.now();
      const healthy = streamConnected && now - lastHeartbeat < 30_000;
      if (now - lastSuccess > (healthy ? 30_000 : 10_000)) setDisplay(null);
      if (!healthy || now - lastSuccess >= 15_000) void refresh();
    }, 1500);
    return () => {
      controller.abort();
      clearInterval(timer);
      clearTimeout(reconnectTimer);
      clearTimeout(refreshTimer);
    };
  }, [id]);
  return display ? (
    <LiveOverlay
      display={
        protectedImage && display.product
          ? {
              ...display,
              product: {
                ...display.product,
                imageUrl: image?.source === imageSource ? image.url : null,
              },
            }
          : display
      }
    />
  ) : null;
}
